'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';

type Language = 'en' | 'es';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations = {
  en: {
    // Navigation
    'nav.benefits': 'Benefits',
    'nav.howItWorks': 'How It Works',
    'nav.commission': 'Commission',
    'nav.testimonials': 'Testimonials',
    'nav.community': 'Community',
    'nav.apply': 'Become an Ambassador',
    'nav.program': 'Program',
    'nav.training': 'Training',
    'nav.investment': 'Investment',
    'nav.questions': 'Questions',
    'nav.tooltip.benefits': 'Explore ambassador benefits',
    'nav.tooltip.howItWorks': 'See how the program works',
    'nav.tooltip.commission': 'View commission structure',
    'nav.tooltip.testimonials': 'Real ambassador stories',
    'nav.tooltip.community': 'Our global community',
    'nav.tooltip.apply': 'Start your journey',
    'nav.tooltip.program': 'Discover the complete program',
    'nav.tooltip.training': 'Training details',
    'nav.tooltip.investment': 'Income potential',
    'nav.tooltip.questions': 'Answers to your questions',

    // CTA Buttons
    'cta.createProfile': 'Create My Profile',
    'cta.learnMore': 'Learn More',
    'cta.becomeAmbassador': 'Become an Ambassador',
    'cta.viewCommission': 'View Full Commission Plan',
    'cta.applyNow': 'Apply Now',
    'cta.scheduleCall': 'Schedule Call',
    'cta.watchStories': 'Watch Success Stories',
    'cta.applyToProgram': 'Apply to Program',
    'cta.startTransformation': 'Start your transformation',
    'cta.advisorAvailable': 'Advisor available now',
    'cta.moreInfoBelow': 'More information below',
    'cta.talkToAdvisor': 'Talk to an advisor',

    // Hero Section
    'hero.eyebrow': 'Official Ambassador Program',
    'hero.headline': 'Become an Official',
    'hero.headlineBrand': 'Neuroakashic™ Light Ambassador',
    'hero.subtitle': 'Share a technology that transforms lives while building a purpose-driven business.',
    'hero.badge': 'Certified Ambassador Program',
    'hero.subheadline': 'Transform Lives While Building Your Spiritual Business',
    'hero.description': 'Earn',
    'hero.descriptionAmount': '$200–$8,000 USD in the first month',
    'hero.descriptionEnd': 'while facilitating deep healing and consciousness expansion',
    'hero.activeAmbassadors': 'Active Ambassadors',
    'hero.averageIncome': 'Average Monthly Income',
    'hero.transformations': 'Lives Transformed',
    'hero.profileName': 'Ana Silvia Lara',
    'hero.profileTitle': 'Neuroakashic™ Light Ambassador',
    'hero.profileBio': 'Helping people reconnect with their highest potential through advanced vibrational technology.',
    'hero.profileUrl': 'neuroakashic.com/ambassador/ana-silvia',
    'hero.profileContact': 'Contact',
    'hero.profileRegister': 'Register With Me',

    // Benefits Section
    'benefits.eyebrow': 'Ambassador Benefits',
    'benefits.headline': 'Everything You Need to',
    'benefits.headlineAccent': 'Succeed',
    'benefits.subtitle': 'A complete ecosystem designed to support your growth as a Neuroakashic™ Light Ambassador.',
    'benefits.professionalProfile': 'Professional Profile',
    'benefits.professionalProfileDesc': 'Your own branded ambassador profile page with photo, bio, and credentials.',
    'benefits.personalPage': 'Personal Landing Page',
    'benefits.personalPageDesc': 'A dedicated page to showcase your services and attract new clients.',
    'benefits.referralLink': 'Referral Link',
    'benefits.referralLinkDesc': 'Personalized URL to track registrations and commissions automatically.',
    'benefits.training': 'Training',
    'benefits.trainingDesc': 'Comprehensive certification program and ongoing education resources.',
    'benefits.marketing': 'Marketing Resources',
    'benefits.marketingDesc': 'Ready-to-use assets, templates, and campaigns to grow your audience.',
    'benefits.community': 'Global Community',
    'benefits.communityDesc': 'Connect with ambassadors worldwide for support, collaboration, and growth.',
    'benefits.connect': 'Neuroakashic Connect™',
    'benefits.connectDesc': 'Exclusive platform to manage sessions, clients, and communications.',
    'benefits.support': 'Support',
    'benefits.supportDesc': 'Dedicated team available to guide you at every step of your journey.',
    'benefits.dashboard': 'Dashboard',
    'benefits.dashboardDesc': 'Real-time analytics on referrals, commissions, and performance metrics.',
    'benefits.digitalTools': 'Digital Tools',
    'benefits.digitalToolsDesc': 'Suite of professional tools to manage and scale your ambassador business.',

    // How It Works
    'howItWorks.eyebrow': 'How It Works',
    'howItWorks.headline': 'Four Steps to',
    'howItWorks.headlineAccent': 'Your First Commission',
    'howItWorks.subtitle': 'A simple, proven process designed to get you earning from day one.',
    'howItWorks.step1': 'Share Your Link',
    'howItWorks.step1Desc': 'Share your personalized referral URL across your network and social channels.',
    'howItWorks.step2': 'Schedule a Demo',
    'howItWorks.step2Desc': 'Your referral books a discovery session to experience Neuroakashic™ technology.',
    'howItWorks.step3': 'Customer Registers',
    'howItWorks.step3Desc': 'They choose their package and complete registration through your link.',
    'howItWorks.step4': 'Receive Commission',
    'howItWorks.step4Desc': 'Your commission is processed and deposited automatically to your account.',

    // Profile Section
    'profile.eyebrow': 'Your Ambassador Profile',
    'profile.headline': 'Your Personal',
    'profile.headlineAccent': 'Digital Presence',
    'profile.subtitle': 'Every ambassador receives a professionally designed profile page — your personal hub for attracting and converting clients.',
    'profile.exampleUrl': 'neuroakashic.com/ambassador/your-name',
    'profile.services': 'Services',
    'profile.service1': 'Neuroakashic™ Sessions',
    'profile.service2': 'Quantum Scans',
    'profile.service3': 'Consciousness Coaching',
    'profile.contactBtn': 'Contact',
    'profile.registerBtn': 'Register With Me',
    'profile.feature1': 'Custom URL',
    'profile.feature1Desc': 'Your unique referral link, always active',
    'profile.feature2': 'Social Links',
    'profile.feature2Desc': 'Connect all your social profiles',
    'profile.feature3': 'Live Analytics',
    'profile.feature3Desc': 'Track visits and conversions in real time',

    // Commission Plan
    'commission.eyebrow': 'Commission Structure',
    'commission.headline': 'Your Impact Creates',
    'commission.headlineAccent': 'Opportunity',
    'commission.subtitle': 'Earn commissions for every successful registration made through your personalized referral link.',
    'commission.highlight': 'Earn up to $500 USD from a single sale.',
    'commission.viewFull': 'View Full Commission Plan',
    'commission.hideTable': 'Hide Table',
    'commission.package': 'Package',
    'commission.price': 'Price',
    'commission.tier1': '10%',
    'commission.tier2': '15%',
    'commission.tier3': '20%',
    'commission.upTo': 'Up to',
    'commission.perSale': 'per sale',
    'commission.gaia': 'CUANTICO',
    'commission.crystal': 'CRYSTAL',
    'commission.diamond': 'DIAMOND',
    'commission.elite': 'ELITE',

    // Testimonials
    'testimonials.eyebrow': 'Ambassador Stories',
    'testimonials.headline': 'Voices from Our',
    'testimonials.headlineAccent': 'Global Community',
    'testimonials.subtitle': 'Real ambassadors. Real results. Real transformation.',

    // Global Community
    'community.eyebrow': 'Global Reach',
    'community.headline': 'One Light.',
    'community.headline2': 'One Drop at a Time.',
    'community.headline3': 'One Global Community.',
    'community.subtitle': 'Neuroakashic™ ambassadors are active in over 30 countries, creating a network of transformation that spans every continent.',
    'community.stat1': 'Countries',
    'community.stat2': 'Ambassadors',
    'community.stat3': 'Lives Transformed',

    // Final CTA
    'finalCta.eyebrow': 'Begin Your Journey',
    'finalCta.headline': 'Your Transformation Can Become',
    'finalCta.headlineAccent': 'an Opportunity.',
    'finalCta.subtitle': 'Join a global movement of purpose-driven ambassadors building meaningful businesses while changing lives.',
    'finalCta.button': 'Become an Ambassador',
    'finalCta.note': 'No experience required. Full training provided.',

    // FAQ
    'faq.headline': 'Frequently Asked Questions',
    'faq.subtitle': 'Everything you need to know about the Neuroakashic™ Light Ambassador Program.',

    // Common
    'common.loading': 'Loading...',
  },
  es: {
    // Navigation
    'nav.benefits': 'Beneficios',
    'nav.howItWorks': 'Cómo Funciona',
    'nav.commission': 'Comisiones',
    'nav.testimonials': 'Testimonios',
    'nav.community': 'Comunidad',
    'nav.apply': 'Ser Embajador',
    'nav.program': 'Programa',
    'nav.training': 'Entrenamiento',
    'nav.investment': 'Inversión',
    'nav.questions': 'Preguntas',
    'nav.tooltip.benefits': 'Explora los beneficios',
    'nav.tooltip.howItWorks': 'Cómo funciona el programa',
    'nav.tooltip.commission': 'Ver estructura de comisiones',
    'nav.tooltip.testimonials': 'Historias reales de embajadores',
    'nav.tooltip.community': 'Nuestra comunidad global',
    'nav.tooltip.apply': 'Inicia tu camino',
    'nav.tooltip.program': 'Descubre el programa completo',
    'nav.tooltip.training': 'Detalles del entrenamiento',
    'nav.tooltip.investment': 'Potencial de ingresos',
    'nav.tooltip.questions': 'Respuestas a tus dudas',

    // CTA Buttons
    'cta.createProfile': 'Crear Mi Perfil',
    'cta.learnMore': 'Conocer Más',
    'cta.becomeAmbassador': 'Ser Embajador',
    'cta.viewCommission': 'Ver Plan Completo de Comisiones',
    'cta.applyNow': 'Aplicar Ahora',
    'cta.scheduleCall': 'Agendar Llamada',
    'cta.watchStories': 'Ver Historias de Éxito',
    'cta.applyToProgram': 'Aplicar al Programa',
    'cta.startTransformation': 'Inicia tu transformación',
    'cta.advisorAvailable': 'Asesor disponible ahora',
    'cta.moreInfoBelow': 'Más información abajo',
    'cta.talkToAdvisor': 'Habla con un asesor',

    // Hero Section
    'hero.eyebrow': 'Programa Oficial de Embajadores',
    'hero.headline': 'Conviértete en Embajador',
    'hero.headlineBrand': 'Neuroakashic™ Oficial',
    'hero.subtitle': 'Comparte una tecnología que transforma vidas mientras construyes un negocio con propósito.',
    'hero.badge': 'Programa de Embajadores Certificados',
    'hero.subheadline': 'Transforma Vidas Mientras Construyes Tu Negocio',
    'hero.description': 'Gana',
    'hero.descriptionAmount': '$200–$8,000 USD en el primer mes',
    'hero.descriptionEnd': 'facilitando sanación profunda y expansión de consciencia',
    'hero.activeAmbassadors': 'Embajadores Activos',
    'hero.averageIncome': 'Ingreso Mensual Promedio',
    'hero.transformations': 'Vidas Transformadas',
    'hero.profileName': 'Ana Silvia Lara',
    'hero.profileTitle': 'Embajadora Neuroakashic™ Light',
    'hero.profileBio': 'Ayudando a las personas a reconectarse con su mayor potencial a través de tecnología vibracional avanzada.',
    'hero.profileUrl': 'neuroakashic.com/embajador/ana-silvia',
    'hero.profileContact': 'Contactar',
    'hero.profileRegister': 'Registrarme',

    // Benefits Section
    'benefits.eyebrow': 'Beneficios del Embajador',
    'benefits.headline': 'Todo lo que Necesitas para',
    'benefits.headlineAccent': 'Triunfar',
    'benefits.subtitle': 'Un ecosistema completo diseñado para apoyar tu crecimiento como Embajador Neuroakashic™ Light.',
    'benefits.professionalProfile': 'Perfil Profesional',
    'benefits.professionalProfileDesc': 'Tu propia página de embajador con foto, biografía y credenciales.',
    'benefits.personalPage': 'Página Personal',
    'benefits.personalPageDesc': 'Una página dedicada para mostrar tus servicios y atraer nuevos clientes.',
    'benefits.referralLink': 'Enlace de Referido',
    'benefits.referralLinkDesc': 'URL personalizada para rastrear registros y comisiones automáticamente.',
    'benefits.training': 'Entrenamiento',
    'benefits.trainingDesc': 'Programa de certificación completo y recursos de educación continua.',
    'benefits.marketing': 'Recursos de Marketing',
    'benefits.marketingDesc': 'Materiales, plantillas y campañas listas para usar y hacer crecer tu audiencia.',
    'benefits.community': 'Comunidad Global',
    'benefits.communityDesc': 'Conéctate con embajadores de todo el mundo para apoyo, colaboración y crecimiento.',
    'benefits.connect': 'Neuroakashic Connect™',
    'benefits.connectDesc': 'Plataforma exclusiva para gestionar sesiones, clientes y comunicaciones.',
    'benefits.support': 'Soporte',
    'benefits.supportDesc': 'Equipo dedicado disponible para guiarte en cada paso de tu camino.',
    'benefits.dashboard': 'Dashboard',
    'benefits.dashboardDesc': 'Analíticas en tiempo real sobre referidos, comisiones y métricas de rendimiento.',
    'benefits.digitalTools': 'Herramientas Digitales',
    'benefits.digitalToolsDesc': 'Suite de herramientas profesionales para gestionar y escalar tu negocio.',

    // How It Works
    'howItWorks.eyebrow': 'Cómo Funciona',
    'howItWorks.headline': 'Cuatro Pasos hacia',
    'howItWorks.headlineAccent': 'Tu Primera Comisión',
    'howItWorks.subtitle': 'Un proceso simple y probado diseñado para que empieces a ganar desde el primer día.',
    'howItWorks.step1': 'Comparte Tu Enlace',
    'howItWorks.step1Desc': 'Comparte tu URL de referido personalizada en tu red y canales sociales.',
    'howItWorks.step2': 'Agenda una Demo',
    'howItWorks.step2Desc': 'Tu referido reserva una sesión de descubrimiento para experimentar la tecnología Neuroakashic™.',
    'howItWorks.step3': 'El Cliente se Registra',
    'howItWorks.step3Desc': 'Elige su paquete y completa el registro a través de tu enlace.',
    'howItWorks.step4': 'Recibe Tu Comisión',
    'howItWorks.step4Desc': 'Tu comisión se procesa y deposita automáticamente en tu cuenta.',

    // Profile Section
    'profile.eyebrow': 'Tu Perfil de Embajador',
    'profile.headline': 'Tu Presencia',
    'profile.headlineAccent': 'Digital Personal',
    'profile.subtitle': 'Cada embajador recibe una página de perfil diseñada profesionalmente — tu centro personal para atraer y convertir clientes.',
    'profile.exampleUrl': 'neuroakashic.com/embajador/tu-nombre',
    'profile.services': 'Servicios',
    'profile.service1': 'Sesiones Neuroakashic™',
    'profile.service2': 'Escaneos Cuánticos',
    'profile.service3': 'Coaching de Consciencia',
    'profile.contactBtn': 'Contactar',
    'profile.registerBtn': 'Registrarme',
    'profile.feature1': 'URL Personalizada',
    'profile.feature1Desc': 'Tu enlace único de referido, siempre activo',
    'profile.feature2': 'Redes Sociales',
    'profile.feature2Desc': 'Conecta todos tus perfiles sociales',
    'profile.feature3': 'Analíticas en Vivo',
    'profile.feature3Desc': 'Rastrea visitas y conversiones en tiempo real',

    // Commission Plan
    'commission.eyebrow': 'Estructura de Comisiones',
    'commission.headline': 'Tu Impacto Crea',
    'commission.headlineAccent': 'Oportunidad',
    'commission.subtitle': 'Gana comisiones por cada registro exitoso realizado a través de tu enlace de referido personalizado.',
    'commission.highlight': 'Gana hasta $500 USD de una sola venta.',
    'commission.viewFull': 'Ver Plan Completo de Comisiones',
    'commission.hideTable': 'Ocultar Tabla',
    'commission.package': 'Paquete',
    'commission.price': 'Precio',
    'commission.tier1': '10%',
    'commission.tier2': '15%',
    'commission.tier3': '20%',
    'commission.upTo': 'Hasta',
    'commission.perSale': 'por venta',
    'commission.gaia': 'CUANTICO',
    'commission.crystal': 'CRYSTAL',
    'commission.diamond': 'DIAMOND',
    'commission.elite': 'ELITE',

    // Testimonials
    'testimonials.eyebrow': 'Historias de Embajadores',
    'testimonials.headline': 'Voces de Nuestra',
    'testimonials.headlineAccent': 'Comunidad Global',
    'testimonials.subtitle': 'Embajadores reales. Resultados reales. Transformación real.',

    // Global Community
    'community.eyebrow': 'Alcance Global',
    'community.headline': 'Una Luz.',
    'community.headline2': 'Una Gota a la Vez.',
    'community.headline3': 'Una Comunidad Global.',
    'community.subtitle': 'Los embajadores Neuroakashic™ están activos en más de 30 países, creando una red de transformación que abarca todos los continentes.',
    'community.stat1': 'Países',
    'community.stat2': 'Embajadores',
    'community.stat3': 'Vidas Transformadas',

    // Final CTA
    'finalCta.eyebrow': 'Comienza Tu Camino',
    'finalCta.headline': 'Tu Transformación Puede Convertirse',
    'finalCta.headlineAccent': 'en una Oportunidad.',
    'finalCta.subtitle': 'Únete a un movimiento global de embajadores con propósito que construyen negocios significativos mientras cambian vidas.',
    'finalCta.button': 'Ser Embajador',
    'finalCta.note': 'Sin experiencia previa. Entrenamiento completo incluido.',

    // FAQ
    'faq.headline': 'Preguntas Frecuentes',
    'faq.subtitle': 'Todo lo que necesitas saber sobre el Programa de Embajadores Neuroakashic™ Light.',

    // Common
    'common.loading': 'Cargando...',
  }
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>('en');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('neuroakashic-language') as Language;
    if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'es')) {
      setLanguageState(savedLanguage);
    }
  }, []);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('neuroakashic-language', lang);
  };

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations['en']] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};